// Administration — FA-S1 / FA-S2 / FA-S3
// Users tab wired to Core API. Simulation Control tab wired to simulation server.
// Data Feeds and Audit Log tabs wired to Crowd Admin facades.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useApp } from '@/shell/AppContext';
import {
  type ColorPalette, Icons, KLabel, StatusDot, Panel, Btn, Tabs, StatusColor,
} from '@vigilo/design-system';
import { getCrowdC, operationStatusColor } from '../../design/crowdPalette';
import { useMockMode } from '@/shell/MockModeContext';
import { useUsers, generateTempPassword } from '../hooks/useUsers';
import type { CreateUserPayload, UpdateUserPayload } from '../hooks/useUsers';
import { useTeams } from '../hooks/useTeams';
import type { CrowdTeam, TeamPayload } from '../hooks/useTeams';
import { useOperations } from '../hooks/useOperations';
import { useSimulation, ANOMALY_SCHEDULE } from '../hooks/useSimulation';
import { useCrowdTaxonomy } from '../hooks/useCrowdTaxonomy';
import type { CrowdZonePreset } from '../hooks/useCrowdTaxonomy';
import { useAdminDataFeeds } from '../hooks/useAdminDataFeeds';
import type { CrowdDataFeedStatus, CrowdDataFeedsResponse } from '../hooks/useAdminDataFeeds';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import type {
  CrowdAdminAuditLogEntry,
  CrowdAdminAuditExportFormat,
  CrowdAdminAuditLogFilters,
  CrowdAdminAuditLogResponse,
} from '../hooks/useAdminAuditLog';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useVenues } from '../hooks/useVenues';
import { useZones } from '../hooks/useZones';
import { useLiveZoneMetrics, zoneMetricsDensitySnapshots } from '../hooks/useLiveZoneMetrics';
import { useDensityData } from '../data/useMockData';
import { MOCK_ZONES } from '../data/mock';
import { useToast, ToastContainer } from '../components/Toast';
import { PaginationControls } from '../components/PaginationControls';
import type { CrowdPaginationEnvelope } from '../components/PaginationControls';
import { useLocalPagination } from '../hooks/useLocalPagination';
import { isoToLocalDateTimeFieldValue, localDateTimeFieldToIso } from '../utils/local-date-time-range';
import { VenuesScreen } from './venues';
import { ZonesScreen } from './zones';
import type { Operation, User } from '@vigilo/shared';

// ── Shared helpers ────────────────────────────────────────────────────────────

function roleLabel(role: User['role']): string {
  if (role === 'crowd_analyst')      return 'Crowd Analyst';
  if (role === 'command_supervisor') return 'Command Supervisor';
  if (role === 'system_admin')       return 'System Admin';
  return role;
}

function roleColor(C: ColorPalette, role: User['role']): string {
  if (role === 'crowd_analyst')      return C.teal;
  if (role === 'command_supervisor') return C.amber;
  if (role === 'system_admin')       return C.ember;
  return C.muted;
}

function userInitials(u: User): string {
  const parts = u.displayName.split(' ');
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}

const CORE_AUTH_ACTION_LABELS: Record<string, string> = {
  'core.auth.login_success': 'signed in',
  'core.auth.logout': 'signed out',
  'core.auth.token_refresh': 'refreshed the session',
  'core.auth.session_expired': 'had the session expire',
  'core.auth.login_failure': 'sign-in attempt failed',
};

function auditActorDisplayName(entry: CrowdAdminAuditLogEntry, userById: ReadonlyMap<string, User>): string {
  const actor = entry.actor?.trim();
  if (!actor || actor === 'system') return 'System';
  if (actor === 'anonymous') return 'System';
  const user = userById.get(actor);
  if (user?.displayName?.trim()) return user.displayName.trim();
  if (entry.actorRole === 'system_admin' || entry.actorRole === 'command_supervisor' || entry.actorRole === 'crowd_analyst') {
    return roleLabel(entry.actorRole);
  }
  return 'Platform user';
}

function auditActorRoleDisplay(entry: CrowdAdminAuditLogEntry): string {
  if (entry.actorRole === 'system_admin' || entry.actorRole === 'command_supervisor' || entry.actorRole === 'crowd_analyst') {
    return roleLabel(entry.actorRole);
  }
  if (entry.actorType === 'anonymous') return 'System';
  return entry.actorRole?.trim() || 'System';
}

function auditActionSentence(entry: CrowdAdminAuditLogEntry, userById: ReadonlyMap<string, User>): string {
  const authAction = CORE_AUTH_ACTION_LABELS[entry.action];
  if (entry.resourceType === 'core.session' && authAction) {
    return `${auditActorDisplayName(entry, userById)} ${authAction}`;
  }
  return entry.detail;
}

function crowdRoleDefinitions(C: ColorPalette) {
  return [
    {
      key: 'crowd_analyst' as User['role'],
      name: 'Crowd Operations Analyst', color: C.teal,
      can: [
        'Monitor live map dashboard',
        'View and acknowledge alerts',
        'Escalate alerts to supervisor',
        'Create and configure zones',
        'Flag anonymous tokens (max 20/session)',
        'Export flagged token summaries',
        'Run and export reports',
      ],
      cannot: ['Resolve alerts', 'Modify org or user configuration', 'Access Core admin UI'],
    },
    {
      key: 'command_supervisor' as User['role'],
      name: 'Command Supervisor', color: C.amber,
      can: [
        'All Analyst capabilities',
        'View Command Dashboard',
        'Resolve and close alerts',
        'Override alert escalation',
        'View full alert history across all zones',
        'View flagged token panels for team',
      ],
      cannot: ['Modify system configuration', 'Access Core admin UI'],
    },
    {
      key: 'system_admin' as User['role'],
      name: 'System Administrator', color: C.ember,
      can: [
        'Full Core admin UI access',
        'User and role management',
        'Organisation and jurisdiction config',
        'Zone type preset management',
        'Data feed configuration',
        'Audit log access',
      ],
      cannot: ['Operational map and alert functions (admin is not an operational role)'],
    },
  ];
}

function Field({ C, label, children }: { C: ColorPalette; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const inputStyle = (C: ColorPalette): React.CSSProperties => ({
  width: '100%', padding: '9px 12px', fontSize: 12.5,
  background: C.surface, color: C.text, border: `1px solid ${C.border2}`,
  borderRadius: 4, fontFamily: "'Albert Sans', sans-serif", outline: 'none', boxSizing: 'border-box',
});

function ModalOverlay({ C, onClose, children, title, width = 440 }: {
  C: ColorPalette; onClose: () => void; children: React.ReactNode; title: string; width?: number;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 8,
        width, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 4 }}>
            <Icons.X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

type RoleFilter = 'all' | User['role'];

interface UsersTabProps {
  C:          ColorPalette;
  users:      User[];
  loading:    boolean;
  error:      string | null;
  createUser: (p: CreateUserPayload) => Promise<void>;
  updateUser: (id: string, p: UpdateUserPayload) => Promise<void>;
  deactivate: (id: string) => Promise<void>;
  reactivate: (id: string) => Promise<void>;
  onToast:    (msg: string, variant: 'success' | 'error') => void;
}

function UsersTab({ C, users, loading, error, createUser, updateUser, deactivate, reactivate, onToast }: UsersTabProps) {
  const [roleFilter,       setRoleFilter]       = useState<RoleFilter>('all');
  const [inviteOpen,       setInviteOpen]       = useState(false);
  const [inviteForm,       setInviteForm]       = useState<{ displayName: string; email: string; role: User['role'] }>({
    displayName: '', email: '', role: 'crowd_analyst',
  });
  const [invitePw,         setInvitePw]         = useState<string | null>(null);
  const [inviting,         setInviting]         = useState(false);
  const [editUser,         setEditUser]         = useState<User | null>(null);
  const [editForm,         setEditForm]         = useState<{ displayName: string; role: User['role'] }>({
    displayName: '', role: 'crowd_analyst',
  });
  const [editing,          setEditing]          = useState(false);
  const [confirmToggle,    setConfirmToggle]    = useState<User | null>(null);
  const [toggling,         setToggling]         = useState(false);

  const openEdit = useCallback((u: User) => {
    setEditUser(u);
    setEditForm({ displayName: u.displayName, role: u.role });
  }, []);

  const closeInvite = useCallback(() => {
    setInviteOpen(false);
    setInvitePw(null);
    setInviteForm({ displayName: '', email: '', role: 'crowd_analyst' });
  }, []);

  const handleInvite = useCallback(async () => {
    if (!inviteForm.displayName || !inviteForm.email) return;
    const pw = generateTempPassword();
    setInviting(true);
    try {
      await createUser({ ...inviteForm, password: pw });
      setInvitePw(pw);
      onToast(`${inviteForm.displayName} invited.`, 'success');
    } catch {
      onToast('Failed to invite user.', 'error');
    } finally {
      setInviting(false);
    }
  }, [inviteForm, createUser, onToast]);

  const handleEdit = useCallback(async () => {
    if (!editUser) return;
    setEditing(true);
    try {
      await updateUser(editUser.id, editForm);
      onToast('User updated.', 'success');
      setEditUser(null);
    } catch {
      onToast('Failed to update user.', 'error');
    } finally {
      setEditing(false);
    }
  }, [editUser, editForm, updateUser, onToast]);

  const handleToggle = useCallback(async () => {
    if (!confirmToggle) return;
    setToggling(true);
    try {
      if (confirmToggle.active) {
        await deactivate(confirmToggle.id);
        onToast(`${confirmToggle.displayName} deactivated.`, 'success');
      } else {
        await reactivate(confirmToggle.id);
        onToast(`${confirmToggle.displayName} reactivated.`, 'success');
      }
      setConfirmToggle(null);
    } catch {
      onToast('Failed to update user status.', 'error');
    } finally {
      setToggling(false);
    }
  }, [confirmToggle, deactivate, reactivate, onToast]);

  const filtered = roleFilter === 'all' ? users : users.filter(u => u.role === roleFilter);
  const userPagination = useLocalPagination(filtered);

  useEffect(() => {
    userPagination.resetPage();
  }, [roleFilter, userPagination.resetPage]);

  const filterPills: { id: RoleFilter; label: string }[] = [
    { id: 'all',                label: `All (${users.length})`  },
    { id: 'crowd_analyst',      label: `Analyst (${users.filter(u => u.role === 'crowd_analyst').length})`      },
    { id: 'command_supervisor', label: `Supervisor (${users.filter(u => u.role === 'command_supervisor').length})` },
    { id: 'system_admin',       label: `Admin (${users.filter(u => u.role === 'system_admin').length})`         },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>User Management</div>
          {error && <span style={{ fontSize: 10.5, color: C.ember }}>— {error}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {filterPills.map(p => (
              <button key={p.id} onClick={() => setRoleFilter(p.id)} style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, cursor: 'pointer',
                background: roleFilter === p.id ? C.amberDim : 'transparent',
                border: `1px solid ${roleFilter === p.id ? C.amber + '55' : C.border2}`,
                color: roleFilter === p.id ? C.amber : C.sub,
                fontFamily: "'Albert Sans', sans-serif",
              }}>{p.label}</button>
            ))}
          </div>
          <Btn C={C} variant="primary" icon={<Icons.Plus size={12} />} onClick={() => setInviteOpen(true)}>
            Invite User
          </Btn>
        </div>
      </div>

      <Panel C={C}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading users…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['User', 'Email', 'Role', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {userPagination.pageRows.map(u => {
                const rc = roleColor(C, u.role);
                return (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: `${rc}22`, border: `1px solid ${rc}55`, color: rc,
                          fontSize: 10, fontWeight: 700,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>{userInitials(u)}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>
                          {u.displayName}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 11, color: C.sub, fontFamily: 'monospace' }}>{u.email}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
                        background: `${rc}22`, color: rc, letterSpacing: '0.04em',
                      }}>{roleLabel(u.role)}</span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <StatusDot color={u.active ? C.green : C.muted} pulse={u.active} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: u.active ? C.green : C.muted, textTransform: 'capitalize' }}>
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Btn C={C} variant="ghost" size="sm" icon={<Icons.Edit size={11} />} onClick={() => openEdit(u)}>Edit</Btn>
                        <Btn C={C} variant="ghost" size="sm" onClick={() => setConfirmToggle(u)}>
                          {u.active ? 'Deactivate' : 'Reactivate'}
                        </Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 12 }}>No users match this filter.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <PaginationControls
          C={C}
          pagination={userPagination.pagination}
          loading={loading}
          error={error}
          emptyLabel="No users match this filter."
          onPageChange={userPagination.setPage}
          onPageSizeChange={userPagination.setPageSize}
        />
      </Panel>

      {/* Invite Modal */}
      {inviteOpen && (
        <ModalOverlay C={C} title="Invite User" onClose={closeInvite}>
          {invitePw ? (
            <div>
              <div style={{
                background: `${C.green}18`, border: `1px solid ${C.green}40`,
                borderRadius: 6, padding: '14px 16px', marginBottom: 20,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.green, marginBottom: 6 }}>User invited successfully</div>
                <div style={{ fontSize: 11, color: C.sub, lineHeight: 1.5 }}>
                  Share this temporary password with the user. It will not be shown again.
                </div>
                <div style={{
                  marginTop: 12, padding: '8px 12px', background: C.surface, borderRadius: 4,
                  fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: C.amber,
                  border: `1px solid ${C.border2}`, letterSpacing: '0.05em',
                }}>{invitePw}</div>
              </div>
              <Btn C={C} variant="primary" onClick={closeInvite} style={{ width: '100%' }}>Done</Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field C={C} label="Display name">
                <input style={inputStyle(C)} value={inviteForm.displayName}
                  onChange={e => setInviteForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="e.g. Sam Chen" />
              </Field>
              <Field C={C} label="Email address">
                <input type="email" style={inputStyle(C)} value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} />
              </Field>
              <Field C={C} label="Role">
                <select style={inputStyle(C)} value={inviteForm.role}
                  onChange={e => setInviteForm(f => ({ ...f, role: e.target.value as User['role'] }))}>
                  <option value="crowd_analyst">Crowd Analyst</option>
                  <option value="command_supervisor">Command Supervisor</option>
                  <option value="system_admin">System Admin</option>
                </select>
              </Field>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Btn C={C} variant="ghost" onClick={closeInvite} style={{ flex: 1 }}>Cancel</Btn>
                <Btn C={C} variant="primary" onClick={handleInvite} disabled={inviting || !inviteForm.displayName || !inviteForm.email} style={{ flex: 1 }}>
                  {inviting ? 'Inviting…' : 'Send Invite'}
                </Btn>
              </div>
            </div>
          )}
        </ModalOverlay>
      )}

      {/* Edit Modal */}
      {editUser && (
        <ModalOverlay C={C} title={`Edit — ${editUser.displayName}`} onClose={() => setEditUser(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field C={C} label="Display name">
              <input style={inputStyle(C)} value={editForm.displayName}
                onChange={e => setEditForm(f => ({ ...f, displayName: e.target.value }))} />
            </Field>
            <Field C={C} label="Role">
              <select style={inputStyle(C)} value={editForm.role}
                onChange={e => setEditForm(f => ({ ...f, role: e.target.value as User['role'] }))}>
                <option value="crowd_analyst">Crowd Analyst</option>
                <option value="command_supervisor">Command Supervisor</option>
                <option value="system_admin">System Admin</option>
              </select>
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Btn C={C} variant="ghost" onClick={() => setEditUser(null)} style={{ flex: 1 }}>Cancel</Btn>
              <Btn C={C} variant="primary" onClick={handleEdit} disabled={editing} style={{ flex: 1 }}>
                {editing ? 'Saving…' : 'Save Changes'}
              </Btn>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Deactivate/Reactivate Confirmation */}
      {confirmToggle && (
        <ModalOverlay C={C} title={confirmToggle.active ? 'Deactivate User' : 'Reactivate User'} onClose={() => setConfirmToggle(null)} width={380}>
          <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 20 }}>
            {confirmToggle.active
              ? `${confirmToggle.displayName} will lose access immediately. They can be reactivated at any time.`
              : `${confirmToggle.displayName} will regain access to the platform.`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn C={C} variant="ghost" onClick={() => setConfirmToggle(null)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn C={C} variant={confirmToggle.active ? 'danger' : 'primary'} onClick={handleToggle} disabled={toggling} style={{ flex: 1 }}>
              {toggling ? '…' : confirmToggle.active ? 'Deactivate' : 'Reactivate'}
            </Btn>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

// ── Roles tab ─────────────────────────────────────────────────────────────────

// Teams tab

interface TeamAssignmentHistoryItem {
  operationId: string;
  operationName: string;
  operationStatus: Operation['status'];
  assignedZoneCount: number;
  assignmentLabel: string;
  scheduledStart: string;
  scheduledEnd: string;
  updatedAt: string;
}

interface TeamsTabProps {
  C: ColorPalette;
  teams: CrowdTeam[];
  operations: Operation[];
  pagination: CrowdPaginationEnvelope;
  users: User[];
  loading: boolean;
  operationsLoading: boolean;
  error: string | null;
  operationsError: string | null;
  canArchive: boolean;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  createTeam: (payload: TeamPayload) => Promise<void>;
  updateTeam: (id: string, payload: TeamPayload) => Promise<void>;
  archiveTeam: (id: string) => Promise<void>;
  onToast: (msg: string, variant: 'success' | 'error') => void;
}

type TeamFormState = {
  name: string;
  description: string;
  color: string;
  memberUserIds: string[];
};

const EMPTY_TEAM_FORM: TeamFormState = {
  name: '',
  description: '',
  color: '',
  memberUserIds: [],
};

function TeamsTab({
  C,
  teams,
  operations,
  pagination,
  users,
  loading,
  operationsLoading,
  error,
  operationsError,
  canArchive,
  setPage,
  setPageSize,
  createTeam,
  updateTeam,
  archiveTeam,
  onToast,
}: TeamsTabProps) {
  const activeUsers = users.filter(user => user.active);
  const userById = new Map(users.map(user => [user.id, user]));
  const assignmentHistoryByTeamId = useMemo(() => {
    return new Map(teams.map(team => [team.id, teamAssignmentHistoryForTeam(team, operations)]));
  }, [operations, teams]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTeam, setEditTeam] = useState<CrowdTeam | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<CrowdTeam | null>(null);
  const [form, setForm] = useState<TeamFormState>(EMPTY_TEAM_FORM);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const openCreate = useCallback(() => {
    setForm(EMPTY_TEAM_FORM);
    setCreateOpen(true);
  }, []);

  const openEdit = useCallback((team: CrowdTeam) => {
    setEditTeam(team);
    setForm({
      name: team.name,
      description: team.description ?? '',
      color: team.color ?? '',
      memberUserIds: team.memberUserIds,
    });
  }, []);

  const closeForm = useCallback(() => {
    setCreateOpen(false);
    setEditTeam(null);
    setForm(EMPTY_TEAM_FORM);
  }, []);

  const toggleMember = useCallback((userId: string) => {
    setForm(prev => ({
      ...prev,
      memberUserIds: prev.memberUserIds.includes(userId)
        ? prev.memberUserIds.filter(id => id !== userId)
        : [...prev.memberUserIds, userId],
    }));
  }, []);

  const submitForm = useCallback(async () => {
    if (!form.name.trim()) return;
    const payload: TeamPayload = {
      name: form.name,
      description: form.description || null,
      color: form.color || null,
      memberUserIds: form.memberUserIds,
    };
    setSaving(true);
    try {
      if (editTeam) {
        await updateTeam(editTeam.id, payload);
        onToast('Team updated.', 'success');
      } else {
        await createTeam(payload);
        onToast('Team created.', 'success');
      }
      closeForm();
    } catch {
      onToast(editTeam ? 'Failed to update team.' : 'Failed to create team.', 'error');
    } finally {
      setSaving(false);
    }
  }, [closeForm, createTeam, editTeam, form, onToast, updateTeam]);

  const confirmArchive = useCallback(async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await archiveTeam(archiveTarget.id);
      onToast(`${archiveTarget.name} archived.`, 'success');
      setArchiveTarget(null);
    } catch {
      onToast('Failed to archive team.', 'error');
    } finally {
      setArchiving(false);
    }
  }, [archiveTarget, archiveTeam, onToast]);

  const formOpen = createOpen || Boolean(editTeam);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Team Management</div>
          <div style={{ fontSize: 10.5, color: error ? C.ember : C.muted, marginTop: 3 }}>
            {error ?? 'Crowd-owned teams with Core IAM user references.'}
          </div>
        </div>
        <Btn C={C} variant="primary" icon={<Icons.Plus size={12} />} onClick={openCreate}>
          New Team
        </Btn>
      </div>

      <Panel C={C}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading teams...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['Team', 'Members', 'Status', 'Assignment History', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teams.map(team => {
                const color = team.color || C.teal;
                const members = team.memberUserIds
                  .map(id => userById.get(id))
                  .filter((user): user is User => Boolean(user));
                const assignmentHistory = assignmentHistoryByTeamId.get(team.id) ?? [];
                return (
                  <tr key={team.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          width: 28, height: 28, borderRadius: 4, background: `${color}22`,
                          border: `1px solid ${color}55`, color, fontSize: 10, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {team.name.split(' ')[0]?.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{team.name}</div>
                          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{team.description ?? 'No description'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {members.slice(0, 4).map(member => (
                          <span key={member.id} title={member.displayName} style={{
                            width: 24, height: 24, borderRadius: '50%', background: C.surface,
                            border: `1px solid ${C.border2}`, color: C.sub, fontSize: 9,
                            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {userInitials(member)}
                          </span>
                        ))}
                        <span style={{ fontSize: 11, color: C.sub }}>{team.memberUserIds.length} assigned</span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <StatusDot color={team.status === 'active' ? C.green : C.muted} pulse={team.status === 'active'} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: team.status === 'active' ? C.green : C.muted, textTransform: 'capitalize' }}>
                          {team.status}
                        </span>
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px', minWidth: 260 }}>
                      <TeamAssignmentHistoryCell
                        C={C}
                        history={assignmentHistory}
                        loading={operationsLoading}
                        error={operationsError}
                      />
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <Btn C={C} variant="ghost" size="sm" icon={<Icons.Edit size={11} />} onClick={() => openEdit(team)}>Edit</Btn>
                        {canArchive && (
                          <Btn C={C} variant="ghost" size="sm" onClick={() => setArchiveTarget(team)}>Archive</Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {teams.length === 0 && !loading && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 12 }}>No active teams configured.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <PaginationControls
          C={C}
          pagination={pagination}
          loading={loading}
          error={error}
          emptyLabel="No active teams configured."
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Panel>

      {formOpen && (
        <ModalOverlay C={C} title={editTeam ? `Edit - ${editTeam.name}` : 'Create Team'} onClose={closeForm} width={560}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field C={C} label="Team name">
              <input style={inputStyle(C)} value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Alpha Team" />
            </Field>
            <Field C={C} label="Colour">
              <input style={inputStyle(C)} value={form.color}
                onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
                placeholder="#4f9f9b" />
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field C={C} label="Description">
                <textarea style={{ ...inputStyle(C), minHeight: 74, resize: 'vertical' }} value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
              </Field>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field C={C} label="Members">
                <div style={{ border: `1px solid ${C.border2}`, borderRadius: 5, maxHeight: 220, overflowY: 'auto' }}>
                  {activeUsers.map(user => {
                    const checked = form.memberUserIds.includes(user.id);
                    return (
                      <label key={user.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                        borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleMember(user.id)} />
                        <span style={{ flex: 1, fontSize: 12, color: C.text }}>{user.displayName}</span>
                        <span style={{ fontSize: 10, color: C.muted }}>{roleLabel(user.role)}</span>
                      </label>
                    );
                  })}
                  {activeUsers.length === 0 && (
                    <div style={{ padding: 14, color: C.muted, fontSize: 12 }}>No active Crowd users available.</div>
                  )}
                </div>
              </Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <Btn C={C} variant="ghost" onClick={closeForm} style={{ flex: 1 }}>Cancel</Btn>
            <Btn C={C} variant="primary" onClick={submitForm} disabled={saving || !form.name.trim()} style={{ flex: 1 }}>
              {saving ? 'Saving...' : editTeam ? 'Save Team' : 'Create Team'}
            </Btn>
          </div>
        </ModalOverlay>
      )}

      {archiveTarget && (
        <ModalOverlay C={C} title="Archive Team" onClose={() => setArchiveTarget(null)} width={380}>
          <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 20 }}>
            {archiveTarget.name} will be removed from active team assignment lists. Existing operation and zone string assignments are not changed in this checkpoint.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn C={C} variant="ghost" onClick={() => setArchiveTarget(null)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn C={C} variant="danger" onClick={confirmArchive} disabled={archiving} style={{ flex: 1 }}>
              {archiving ? 'Archiving...' : 'Archive'}
            </Btn>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

function teamAssignmentHistoryForTeam(team: CrowdTeam, operations: Operation[]): TeamAssignmentHistoryItem[] {
  return operations
    .map(operation => operationTeamAssignmentHistoryItem(team, operation))
    .filter((item): item is TeamAssignmentHistoryItem => Boolean(item))
    .sort((a, b) => operationHistorySortValue(b) - operationHistorySortValue(a));
}

function operationTeamAssignmentHistoryItem(team: CrowdTeam, operation: Operation): TeamAssignmentHistoryItem | null {
  const assignedZoneIds = new Set(operation.assignedZoneIds);
  const matchedZoneIds = new Set<string>();
  let hasDefaultAssignment = operation.defaultTeamId === team.id;
  let hasZoneAssignment = false;

  for (const assignment of operation.zoneTeamAssignments) {
    if (!assignedZoneIds.has(assignment.zoneId)) continue;

    const inheritedDefault = assignment.inheritedFromDefault && operation.defaultTeamId === team.id;
    if (assignment.teamId === team.id || inheritedDefault) {
      matchedZoneIds.add(assignment.zoneId);
      hasZoneAssignment = !assignment.inheritedFromDefault || hasZoneAssignment;
      hasDefaultAssignment = assignment.inheritedFromDefault || hasDefaultAssignment;
    }
  }

  if (hasDefaultAssignment && operation.zoneTeamAssignments.length === 0) {
    operation.assignedZoneIds.forEach(zoneId => matchedZoneIds.add(zoneId));
  }

  const hasLegacyNameAssignment = operation.assignedTeams.includes(team.name);
  if (!hasDefaultAssignment && !hasZoneAssignment && !hasLegacyNameAssignment) {
    return null;
  }

  return {
    operationId: operation.id,
    operationName: operation.name,
    operationStatus: operation.status,
    assignedZoneCount: matchedZoneIds.size,
    assignmentLabel: teamAssignmentLabel(hasDefaultAssignment, hasZoneAssignment, hasLegacyNameAssignment),
    scheduledStart: operation.scheduledStart,
    scheduledEnd: operation.scheduledEnd,
    updatedAt: operation.updatedAt,
  };
}

function teamAssignmentLabel(
  hasDefaultAssignment: boolean,
  hasZoneAssignment: boolean,
  hasLegacyNameAssignment: boolean,
): string {
  if (hasDefaultAssignment && hasZoneAssignment) return 'Default + zone';
  if (hasDefaultAssignment) return 'Default team';
  if (hasZoneAssignment) return 'Zone assignment';
  if (hasLegacyNameAssignment) return 'Team roster';
  return 'Assigned';
}

function operationHistorySortValue(item: TeamAssignmentHistoryItem): number {
  const date = new Date(item.updatedAt || item.scheduledStart);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function formatTeamHistoryWindow(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
    return 'Schedule unavailable';
  }
  const date = startDate.toLocaleDateString([], { month: 'short', day: '2-digit' });
  const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${date} ${startTime}-${endTime}`;
}

function teamHistoryStatusColor(C: ColorPalette, status: Operation['status']): string {
  if (status === 'active') return C.green;
  if (status === 'paused') return C.amber;
  if (status === 'closed') return C.muted;
  return C.teal;
}

function TeamAssignmentHistoryCell({
  C,
  history,
  loading,
  error,
}: {
  C: ColorPalette;
  history: TeamAssignmentHistoryItem[];
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return <div style={{ fontSize: 10.5, color: C.muted }}>Loading assignment history...</div>;
  }
  if (error) {
    return <div style={{ fontSize: 10.5, color: C.ember }}>{error}</div>;
  }
  if (history.length === 0) {
    return <div style={{ fontSize: 10.5, color: C.muted }}>No operation assignments</div>;
  }

  const activeCount = history.filter(item => item.operationStatus === 'active' || item.operationStatus === 'paused').length;
  return (
    <div style={{ display: 'grid', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: C.text }}>
          {history.length} {history.length === 1 ? 'operation' : 'operations'}
        </span>
        <span style={{ fontSize: 10, color: C.muted }}>
          {activeCount} live
        </span>
      </div>
      {history.slice(0, 3).map(item => {
        const statusColor = teamHistoryStatusColor(C, item.operationStatus);
        return (
          <div key={item.operationId} style={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: statusColor, flexShrink: 0,
              }} />
              <span title={item.operationName} style={{ fontSize: 10.5, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.operationName}
              </span>
            </div>
            <div style={{ fontSize: 9.5, color: C.muted, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ color: statusColor, fontWeight: 700 }}>{item.operationStatus}</span>
              <span>{item.assignmentLabel}</span>
              <span>{item.assignedZoneCount} {item.assignedZoneCount === 1 ? 'zone' : 'zones'}</span>
              <span>{formatTeamHistoryWindow(item.scheduledStart, item.scheduledEnd)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RolesTab({ C, users }: { C: ColorPalette; users: User[] }) {
  const roles = crowdRoleDefinitions(C);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
      {roles.map(r => {
        const count = users.filter(u => u.role === r.key).length;
        return (
          <Panel key={r.key} C={C} style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 6, background: `${r.color}22`,
                color: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Icons.Users size={15} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: C.muted }}>{count} {count === 1 ? 'user' : 'users'} assigned</div>
              </div>
            </div>
            <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: C.green, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Can</div>
              {r.can.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, color: C.sub, padding: '3px 0', lineHeight: 1.4 }}>
                  <span style={{ color: C.green, flexShrink: 0, marginTop: 1 }}><Icons.Check size={11} /></span>
                  <span>{c}</span>
                </div>
              ))}
              <div style={{ fontSize: 9.5, fontWeight: 700, color: C.ember, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 12, marginBottom: 6 }}>Cannot</div>
              {r.cannot.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, fontSize: 11, color: C.muted, padding: '3px 0', lineHeight: 1.4 }}>
                  <span style={{ color: C.ember, flexShrink: 0, marginTop: 1 }}><Icons.X size={11} /></span>
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

// ── Zone Type Presets tab ─────────────────────────────────────────────────────

type PresetRule = {
  alertType?: string;
  thresholds?: Record<string, number>;
  priority?: string;
  autoEscalateAfterMin?: number;
};

const PRESET_ICON_MAP: Record<string, keyof typeof Icons> = {
  'log-in':       'ArrowR',
  users:          'Users',
  armchair:       'Grid',
  'badge-check':  'Shield',
  'move-right':   'ArrowR',
  store:          'Grid',
  'map-pin':      'Pin',
  'shield-alert': 'Shield',
  'scan-line':    'Activity',
  clock:          'Clock',
};

function presetIcon(icon: string | null): keyof typeof Icons {
  return icon ? PRESET_ICON_MAP[icon] ?? 'Shield' : 'Shield';
}

function formatRuleType(alertType: string | undefined): string {
  if (!alertType) return 'Rule';
  return alertType
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatThresholdValue(key: string, value: number): string {
  if (key === 'densityPct' || key === 'surgeRatePct') return `${value}%`;
  if (key === 'timeWindowMin' || key === 'dwellMinutes') return `${value}min`;
  if (key === 'personCount') return `${value} persons`;
  if (key === 'reentryWindowHours') return `${value}h`;
  return String(value);
}

function formatPresetRule(rule: PresetRule): string {
  const thresholdText = Object.entries(rule.thresholds ?? {})
    .map(([key, value]) => formatThresholdValue(key, value))
    .join(' / ');
  const priorityText = rule.priority ? ` - ${rule.priority}` : '';
  const escalationText = rule.autoEscalateAfterMin ? ` - auto ${rule.autoEscalateAfterMin}min` : '';
  return `${formatRuleType(rule.alertType)}${thresholdText ? ` ${thresholdText}` : ''}${priorityText}${escalationText}`;
}

function presetThresholds(preset: CrowdZonePreset): string {
  const rules = preset.rules
    .map(rule => formatPresetRule(rule as PresetRule))
    .filter(Boolean);
  return rules.length > 0 ? rules.join(' | ') : 'Manual configuration required';
}


function ZoneTypePresetsTab({ C, taxonomy, loading, error }: {
  C: ColorPalette;
  taxonomy: ReturnType<typeof useCrowdTaxonomy>['taxonomy'];
  loading: boolean;
  error: string | null;
}) {
  const presets = [...(taxonomy?.zonePresets ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  if (loading) {
    return (
      <Panel C={C}>
        <div style={{ padding: 18, fontSize: 12, color: C.sub }}>Loading Crowd zone presets...</div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel C={C}>
        <div style={{ padding: 18, fontSize: 12, color: C.ember }}>{error}</div>
      </Panel>
    );
  }

  if (presets.length === 0) {
    return (
      <Panel C={C}>
        <div style={{ padding: 18, fontSize: 12, color: C.sub }}>No Crowd zone presets are configured.</div>
      </Panel>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      {presets.map(p => {
        const PresetIcon = Icons[presetIcon(p.icon)] ?? Icons.Shield;
        return (
          <Panel key={p.zoneType} C={C} style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 6, background: C.amberDim,
                border: `1px solid ${C.amber}40`, color: C.amber, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PresetIcon size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.label}</div>
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{p.description}</div>
              </div>
            </div>
            <div style={{ fontSize: 9.5, color: C.muted, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontFamily: 'monospace' }}>
              {presetThresholds(p)}
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

// ── Data Feeds tab ────────────────────────────────────────────────────────────

interface FeedsTabProps {
  C:       ColorPalette;
  data:    CrowdDataFeedsResponse | null;
  loading: boolean;
  error:   string | null;
  refresh: () => Promise<void>;
}

function FeedsTab({ C, data, loading, error, refresh }: FeedsTabProps) {
  const statusColor = (status: CrowdDataFeedStatus): string => {
    if (status === 'configured') return C.green;
    if (status === 'standby') return C.amber;
    return C.muted;
  };

  const statusLabel = (status: CrowdDataFeedStatus): string => status.replace(/_/g, ' ');
  const feeds = data?.feeds ?? [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Data Feed Status</div>
          <div style={{ fontSize: 10.5, color: error ? C.ember : C.muted, marginTop: 2 }}>
            {error ?? (data ? `Facade refreshed ${new Date(data.generatedAt).toLocaleTimeString()}` : 'Crowd Admin facade')}
          </div>
        </div>
        <Btn C={C} variant="ghost" size="sm" icon={<Icons.Refresh size={11} />} onClick={refresh} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Btn>
      </div>
      <Panel C={C} style={{ padding: 0 }}>
        {loading && feeds.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading feed status...</div>
        ) : feeds.map((f, i) => {
          const color = statusColor(f.status);
          return (
            <div key={f.id} style={{
              padding: '14px 18px',
              borderBottom: i < feeds.length - 1 ? `1px solid ${C.border}` : 'none',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <StatusDot color={color} pulse={f.status === 'configured'} size={8} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{f.label}</div>
                <div style={{ fontSize: 10.5, color: C.muted, fontFamily: 'monospace', marginTop: 2 }}>{f.boundary}</div>
                {f.detail && <div style={{ fontSize: 10.5, color: C.sub, marginTop: 2 }}>{f.detail}</div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                  {f.allowedOutputs.map(output => (
                    <span key={output} style={{
                      fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                      background: C.faint, color: C.sub, letterSpacing: '0.04em',
                    }}>{output}</span>
                  ))}
                </div>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, padding: '3px 7px', borderRadius: 3,
                background: `${color}22`, color,
                letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0,
              }}>{statusLabel(f.status)}</span>
            </div>
          );
        })}
        {feeds.length === 0 && !loading && (
          <div style={{ padding: 28, textAlign: 'center', color: C.muted, fontSize: 12 }}>No Crowd feed status rows returned.</div>
        )}
      </Panel>
      <div style={{
        marginTop: 14, background: C.tealDim, border: `1px solid ${C.teal}40`,
        borderRadius: 6, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Icons.Info size={16} />
        <div style={{ fontSize: 11, color: C.sub, lineHeight: 1.5 }}>
          This view is sourced from the Crowd Admin facade. It exposes only anonymous aggregate feed readiness and approved output contracts.
        </div>
      </div>
    </>
  );
}
// ── Simulation Control tab ────────────────────────────────────────────────────

interface SimulationTabProps {
  C:       ColorPalette;
  sim:     ReturnType<typeof useSimulation>;
  onToast: (msg: string, variant: 'success' | 'error') => void;
}

function formatScenario(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatElapsed(ticks: number, intervalMs: number): string {
  const totalSecs = Math.floor((ticks * intervalMs) / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

function SimulationTab({ C, sim, onToast }: SimulationTabProps) {
  const [eventType,      setEventType]      = useState('match');
  const [capacity,       setCapacity]       = useState(40000);
  const [peakWindow,     setPeakWindow]     = useState('kickoff');
  const [confirmReset,   setConfirmReset]   = useState(false);
  const [resetting,      setResetting]      = useState(false);
  const [changingRunState, setChangingRunState] = useState<'start' | 'stop' | null>(null);

  const { isMock: mockMode, toggle: toggleMock } = useMockMode();
  const { snapshots } = useDensityData();
  const { zones: liveZones } = useZones();
  const isDev = (import.meta.env['VITE_APP_ENV'] as string | undefined) === 'development';
  const liveZoneMetrics = useLiveZoneMetrics(null, !mockMode, 2000);
  const liveMetricSnapshots = useMemo(
    () => zoneMetricsDensitySnapshots(liveZoneMetrics.rows),
    [liveZoneMetrics.rows],
  );
  const displayedSnapshots = mockMode ? snapshots : { ...snapshots, ...liveMetricSnapshots };
  const simulationZones = mockMode ? MOCK_ZONES : liveZones;
  const zoneRows = simulationZones.map(z => ({
    code: z.name.substring(0, 3).toUpperCase(),
    name: z.name,
    density: Math.round(displayedSnapshots[z.id]?.densityScore ?? 0),
    occupancy: displayedSnapshots[z.id]?.occupancyCount ?? 0,
  }));

  const handleReset = useCallback(async () => {
    setResetting(true);
    try {
      await sim.reset();
      setConfirmReset(false);
      onToast('Simulation reset to t=0. Operation metric generation restarted.', 'success');
    } catch {
      onToast('Failed to reset simulation.', 'error');
    } finally {
      setResetting(false);
    }
  }, [sim, onToast]);

  const isRunning = sim.status === 'running';
  const operationMetricsRunning = sim.operationMetrics.active;

  const handleMetricState = useCallback(async (nextState: 'start' | 'stop') => {
    setChangingRunState(nextState);
    try {
      if (nextState === 'start') {
        await sim.start();
        onToast('Operation metric generation started.', 'success');
      } else {
        await sim.stop();
        onToast('Operation metric generation stopped.', 'success');
      }
    } catch {
      onToast(`Failed to ${nextState} operation metric generation.`, 'error');
    } finally {
      setChangingRunState(null);
    }
  }, [sim, onToast]);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        {/* Left column */}
        <div>
          {isDev && (
            <Panel C={C} style={{ padding: 18, marginBottom: 12, borderColor: mockMode ? C.amber : C.border2 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <KLabel C={C}>Mock Data Mode</KLabel>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: mockMode ? C.amber : C.text, marginBottom: 4 }}>
                    {mockMode ? 'Mock' : 'Live'}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.muted }}>
                    {mockMode
                      ? 'Screens use local mock data — no backend required.'
                      : 'Screens use Core API and WebSocket streams.'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <StatusDot color={mockMode ? C.amber : C.green} pulse={!mockMode} />
                  <Btn C={C} variant={mockMode ? 'outline' : 'ghost'} size="sm" onClick={() => toggleMock()}>
                    Switch to {mockMode ? 'Live' : 'Mock'}
                  </Btn>
                </div>
              </div>
            </Panel>
          )}

          <Panel C={C} style={{ padding: 18, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <KLabel C={C}>Active Scenario</KLabel>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                  {sim.loading ? '—' : formatScenario(sim.scenario)}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {sim.zoneCount} zones · tick {sim.tick} · {formatElapsed(sim.tick, sim.tickIntervalMs)} elapsed
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  Operation metrics {operationMetricsRunning ? 'running' : 'paused'} - metric tick {sim.operationMetrics.tickCount} - {sim.operationMetrics.operationCount} operations
                </div>
                {sim.error && <div style={{ fontSize: 10.5, color: C.ember, marginTop: 4 }}>{sim.error}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <StatusDot color={isRunning ? C.green : sim.status === 'unknown' ? C.ember : C.muted} pulse={isRunning} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: isRunning ? C.green : sim.status === 'unknown' ? C.ember : C.muted }}>
                  {sim.status}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn C={C} variant="ghost" icon={<Icons.Refresh size={12} />} onClick={() => setConfirmReset(true)}>
                Reset to t=0
              </Btn>
              <Btn
                C={C}
                variant="success"
                icon={<Icons.Play size={12} />}
                onClick={() => { void handleMetricState('start'); }}
                disabled={operationMetricsRunning || changingRunState !== null || sim.loading}
              >
                {changingRunState === 'start' ? 'Starting...' : 'Start generation'}
              </Btn>
              <Btn
                C={C}
                variant="ghost"
                icon={<Icons.Pause size={12} />}
                onClick={() => { void handleMetricState('stop'); }}
                disabled={!operationMetricsRunning || changingRunState !== null || sim.loading}
              >
                {changingRunState === 'stop' ? 'Stopping...' : 'Stop generation'}
              </Btn>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
              {[
                { label: 'Metric state', value: operationMetricsRunning ? 'Running' : 'Paused', color: operationMetricsRunning ? C.green : C.amber },
                { label: 'Metric ticks', value: sim.operationMetrics.tickCount.toLocaleString(), color: C.text },
                { label: 'Tracked zones', value: sim.operationMetrics.zoneStateCount.toLocaleString(), color: C.text },
                { label: 'Anonymous flags', value: sim.operationMetrics.flagStateCount.toLocaleString(), color: C.text },
              ].map(item => (
                <div key={item.label} style={{ minWidth: 0, padding: 10, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 5 }}>
                  <KLabel C={C}>{item.label}</KLabel>
                  <div style={{ fontSize: 13, fontWeight: 700, color: item.color, overflowWrap: 'anywhere' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel C={C} style={{ padding: 18, marginBottom: 12 }}>
            <KLabel C={C}>Scenario Parameters</KLabel>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 10, fontStyle: 'italic' }}>
              Demo parameters — affect scenario labelling only, not simulation engine output.
            </div>
            <Field C={C} label="Event type">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                {[{ v: 'concert', l: 'Concert' }, { v: 'match', l: 'Match' }, { v: 'festival', l: 'Festival' }, { v: 'demo', l: 'Demonstration' }].map(o => (
                  <label key={o.v} style={{
                    padding: '8px 10px', borderRadius: 4, cursor: 'pointer', textAlign: 'center',
                    background: eventType === o.v ? C.amberDim : C.surface,
                    border: `1px solid ${eventType === o.v ? C.amber + '55' : C.border2}`,
                    fontSize: 11, fontWeight: 600, color: eventType === o.v ? C.amber : C.text,
                  }}>
                    <input type="radio" checked={eventType === o.v} onChange={() => setEventType(o.v)} style={{ display: 'none' }} />
                    {o.l}
                  </label>
                ))}
              </div>
            </Field>
            <div style={{ height: 14 }} />
            <Field C={C} label={`Venue capacity: ${capacity.toLocaleString()} persons`}>
              <input type="range" min={5000} max={80000} step={1000} value={capacity}
                onChange={e => setCapacity(+e.target.value)}
                style={{ width: '100%', accentColor: C.amber }} />
            </Field>
            <div style={{ height: 14 }} />
            <Field C={C} label="Peak occupancy window">
              <select value={peakWindow} onChange={e => setPeakWindow(e.target.value)} style={inputStyle(C)}>
                <option value="early">Early (T-90 min)</option>
                <option value="kickoff">Kick-off (T-15 min)</option>
                <option value="halftime">Half-time</option>
                <option value="exit">Post-match exit</option>
              </select>
            </Field>
          </Panel>
        </div>

        {/* Right column */}
        <div>
          <Panel C={C} style={{ padding: 18, marginBottom: 12 }}>
            <KLabel C={C}>Simulated Zones</KLabel>
            {zoneRows.map(z => {
              const status = z.density >= 85 ? 'alert' : z.density >= 70 ? 'warning' : 'normal';
              return (
              <div key={z.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 10, fontFamily: 'monospace', color: C.muted, minWidth: 32 }}>{z.code}</span>
                <span style={{ flex: 1, fontSize: 11.5, color: C.text }}>{z.name}</span>
                <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', minWidth: 54, textAlign: 'right' }}>{z.occupancy.toLocaleString()}</span>
                <div style={{ width: 80, height: 4, background: C.faint, borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${z.density}%`, background: StatusColor(C, status) }} />
                </div>
                <span style={{ fontSize: 10.5, color: C.sub, fontFamily: 'monospace', minWidth: 32, textAlign: 'right' }}>{z.density}%</span>
              </div>
            );})}
          </Panel>

          <Panel C={C} style={{ padding: 18, marginBottom: 12 }}>
            <KLabel C={C}>Anomaly Schedule</KLabel>
            <div style={{ fontSize: 10.5, color: C.muted, marginBottom: 10 }}>
              Scripted anomalies for Champions League match-day scenario.
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {ANOMALY_SCHEDULE.map((a, i) => {
                const isPast = a.tick <= sim.tick;
                const isCurrent = a.tick > sim.tick && a.tick <= sim.tick + 20;
                return (
                  <div key={i} style={{
                    display: 'flex', gap: 10, padding: '7px 0',
                    borderBottom: `1px solid ${C.border}`,
                    opacity: isPast ? 0.45 : 1,
                  }}>
                    <span style={{
                      fontSize: 9.5, fontFamily: 'monospace', color: isCurrent ? C.amber : C.muted,
                      fontWeight: isCurrent ? 700 : 400, minWidth: 48, flexShrink: 0,
                    }}>t={a.tick}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                          background: `${C.amber}22`, color: C.amber,
                          letterSpacing: '0.04em',
                        }}>{a.type}</span>
                        <span style={{ fontSize: 10.5, color: C.sub }}>{a.zone}</span>
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{a.description}</div>
                    </div>
                    {isPast && <span style={{ color: C.green, flexShrink: 0, marginTop: 2 }}><Icons.Check size={11} /></span>}
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>
      </div>

      {/* Reset confirmation modal */}
      {confirmReset && (
        <ModalOverlay C={C} title="Reset Simulation" onClose={() => setConfirmReset(false)} width={380}>
          <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 20 }}>
            This will reset the Crowd simulation to tick 0, clear selected demo operation projections, alerts, and metric state through approved service boundaries, then automatically restart live operation metric generation from the low baseline.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn C={C} variant="ghost" onClick={() => setConfirmReset(false)} style={{ flex: 1 }}>Cancel</Btn>
            <Btn C={C} variant="danger" onClick={handleReset} disabled={resetting} style={{ flex: 1 }}>
              {resetting ? 'Resetting…' : 'Reset to t=0'}
            </Btn>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

// ── Audit Log tab ─────────────────────────────────────────────────────────────

function AuditTab({
  C,
  data,
  loading,
  error,
  filters,
  setFilters,
  refresh,
  requestAuditExport,
  operationId,
  users,
  onToast,
}: {
  C: ColorPalette;
  data: CrowdAdminAuditLogResponse | null;
  loading: boolean;
  error: string | null;
  filters: CrowdAdminAuditLogFilters;
  setFilters: (filters: Partial<CrowdAdminAuditLogFilters>) => void;
  refresh: (filters?: Partial<CrowdAdminAuditLogFilters>) => Promise<void>;
  requestAuditExport: (format: CrowdAdminAuditExportFormat, operationId: string) => Promise<unknown>;
  operationId?: string | null;
  users: User[];
  onToast: (message: string, variant?: 'success' | 'warning' | 'error') => void;
}) {
  const [exportingFormat, setExportingFormat] = useState<CrowdAdminAuditExportFormat | null>(null);
  const entries = data?.entries ?? [];
  const auditUserById = useMemo(
    () => new Map(users.map(user => [user.id, user] as const)),
    [users],
  );
  const options = data?.filterOptions;
  const total = data?.total ?? entries.length;
  const page = data?.page ?? filters.page;
  const pageSize = data?.pageSize ?? filters.pageSize;
  const auditPagination: CrowdPaginationEnvelope = {
    total,
    page,
    pageSize,
    hasNextPage: data?.hasNextPage ?? false,
  };
  useCrowdFilterActivity({
    page: '/admin',
    filterValues: {
      action: filters.action,
      actor: filters.actor,
      from: filters.from ? 'applied' : undefined,
      to: filters.to ? 'applied' : undefined,
      page: page > 1 ? page : undefined,
    },
    searchApplied: Boolean(
      filters.search?.trim()
      || filters.actor?.trim(),
    ),
  });
  const actionTone = (action: string): string => (
    action.includes('archived') ? C.ember :
    action.includes('updated') ? C.amber :
    action.includes('created') ? C.teal :
    C.muted
  );
  const outcomeTone = (outcome: string): string => (
    outcome === 'denied' ? C.ember :
    outcome === 'failure' ? C.amber :
    C.teal
  );
  const optionLabel = (value: string): string => value
    .replace(/^crowd\./, '')
    .replace(/^core\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
  const updateFilter = (patch: Partial<CrowdAdminAuditLogFilters>) => {
    setFilters({ ...patch, page: 1 });
  };
  const queueExport = async (format: CrowdAdminAuditExportFormat) => {
    const scopedOperationId = filters.operationId?.trim() || operationId?.trim() || '';
    if (!scopedOperationId) {
      onToast('Select or filter an operation before queueing audit export', 'warning');
      return;
    }
    setExportingFormat(format);
    try {
      await requestAuditExport(format, scopedOperationId);
      onToast(`Audit ${format.toUpperCase()} queued in Reports`, 'success');
    } catch {
      onToast('Audit export request failed', 'error');
    } finally {
      setExportingFormat(null);
    }
  };
  const inputStyle = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    color: C.text,
    fontSize: 11,
    padding: '7px 9px',
    outline: 'none',
    minWidth: 130,
  } as const;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
  const chipStyle = (color: string) => ({
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 3,
    background: `${color}22`,
    color,
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
  } as const);
  const metaStyle = {
    fontSize: 10,
    color: C.muted,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;
  const sectionLabelStyle = {
    fontSize: 9,
    color: C.muted,
    fontWeight: 700,
    textTransform: 'uppercase',
  } as const;
  const formatDateTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: value, time: '', tz: timezone };
    return {
      date: date.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      tz: timezone,
    };
  };
  const renderValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
  };
  const renderReadableValue = (value: unknown, fallback = 'Not captured'): string => {
    if (value === null || value === undefined) return fallback;
    const rendered = renderValue(value);
    return rendered.length > 80 ? `${rendered.slice(0, 77)}...` : rendered;
  };
  const changedFields = (entry: CrowdAdminAuditLogEntry) => {
    const before = entry.previousState ?? {};
    const after = entry.newState ?? {};
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
    return keys
      .filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
      .slice(0, 6)
      .map(key => ({
        key,
        before: before[key],
        after: after[key],
      }));
  };
  const stateRows = (entry: CrowdAdminAuditLogEntry) => {
    const diffs = changedFields(entry);
    if (diffs.length) return diffs;
    if (!entry.previousState && entry.newState) {
      return [{ key: 'record', before: 'Not created', after: entry.newState }];
    }
    if (entry.previousState && !entry.newState) {
      return [{ key: 'record', before: entry.previousState, after: 'Removed' }];
    }
    return [{ key: 'activity', before: 'Not applicable', after: entry.detail || optionLabel(entry.action) }];
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Immutable Audit Log</div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>Crowd Admin facade over Core Audit - read-only, filtered and redacted</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Download size={11} />} onClick={() => { void queueExport('pdf'); }} disabled={exportingFormat !== null}>
            {exportingFormat === 'pdf' ? 'Queueing...' : 'Queue PDF'}
          </Btn>
          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Download size={11} />} onClick={() => { void queueExport('csv'); }} disabled={exportingFormat !== null}>
            {exportingFormat === 'csv' ? 'Queueing...' : 'Queue CSV'}
          </Btn>
          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Refresh size={11} />} onClick={() => { void refresh(); }}>
            Refresh
          </Btn>
        </div>
      </div>
      <Panel C={C} style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={filters.search ?? ''}
          onChange={e => updateFilter({ search: e.target.value || undefined })}
          placeholder="Search audit"
          style={{ ...inputStyle, minWidth: 190 }}
        />
        <select
          value={filters.actor ?? ''}
          onChange={e => updateFilter({ actor: e.target.value || undefined })}
          style={{ ...inputStyle, minWidth: 180 }}
        >
          <option value="">All users</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>{user.displayName}</option>
          ))}
        </select>
        <select value={filters.action ?? ''} onChange={e => updateFilter({ action: e.target.value || undefined })} style={{ ...inputStyle, minWidth: 170 }}>
          <option value="">All audit events</option>
          {(options?.actions ?? []).map(action => (
            <option key={action} value={action}>{optionLabel(action)}</option>
          ))}
        </select>
        <input
          type="datetime-local"
          value={isoToLocalDateTimeFieldValue(filters.from)}
          onChange={e => updateFilter({ from: localDateTimeFieldToIso(e.target.value) })}
          aria-label="Audit log date range start"
          style={{ ...inputStyle, minWidth: 172 }}
        />
        <input
          type="datetime-local"
          value={isoToLocalDateTimeFieldValue(filters.to)}
          onChange={e => updateFilter({ to: localDateTimeFieldToIso(e.target.value) })}
          aria-label="Audit log date range end"
          style={{ ...inputStyle, minWidth: 172 }}
        />
      </Panel>
      <Panel C={C} style={{ padding: 0 }}>
        {loading && <div style={{ padding: 24, fontSize: 12, color: C.muted }}>Loading audit log...</div>}
        {!loading && error && <div style={{ padding: 24, fontSize: 12, color: C.ember }}>{error}</div>}
        {!loading && !error && entries.length === 0 && (
          <div style={{ padding: 24, fontSize: 12, color: C.muted }}>No Crowd audit entries found.</div>
        )}
        {!loading && !error && entries.map(e => {
          const stamp = formatDateTime(e.occurredAt);
          const tone = actionTone(e.action);
          const rows = stateRows(e);
          const actorDisplayName = auditActorDisplayName(e, auditUserById);
          const actorRoleDisplay = auditActorRoleDisplay(e);
          const actionLabel = optionLabel(e.action);
          const detailDisplay = auditActionSentence(e, auditUserById);
          return (
            <div key={e.id} style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(130px, 0.75fr) minmax(170px, 1fr) minmax(220px, 1.25fr) minmax(220px, 1.2fr) minmax(220px, 1.2fr)',
                gap: 14,
                alignItems: 'start',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={sectionLabelStyle}>When</div>
                  <div style={{ fontSize: 11, color: C.text, fontFamily: 'monospace', marginTop: 4 }}>{stamp.date}</div>
                  <div style={metaStyle}>{stamp.time} {stamp.tz}</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                    <span style={chipStyle(outcomeTone(e.outcome))}>{e.outcome}</span>
                    {e.synthetic && <span style={chipStyle(C.amber)}>Synthetic</span>}
                  </div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={sectionLabelStyle}>User</div>
                  <div style={{ fontSize: 11, color: C.text, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{actorDisplayName}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>{actorRoleDisplay}</div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={sectionLabelStyle}>Audit Event</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    <span style={chipStyle(tone)}>{actionLabel}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{detailDisplay}</div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={sectionLabelStyle}>Previous</div>
                  <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                    {rows.map(diff => (
                      <div key={diff.key} style={{ fontSize: 9.5, color: C.muted, minWidth: 0 }}>
                        <span style={{ color: C.sub, fontFamily: 'monospace' }}>{diff.key}</span>
                        <div style={{ color: C.text, marginTop: 2, overflowWrap: 'anywhere' }}>{renderReadableValue(diff.before)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={sectionLabelStyle}>Current</div>
                  <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                    {rows.map(diff => (
                      <div key={diff.key} style={{ fontSize: 9.5, color: C.muted, minWidth: 0 }}>
                        <span style={{ color: C.sub, fontFamily: 'monospace' }}>{diff.key}</span>
                        <div style={{ color: C.text, marginTop: 2, overflowWrap: 'anywhere' }}>{renderReadableValue(diff.after)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Panel>
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 9.5, color: C.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icons.Lock size={11} /> Append-only Core Audit entries. Exports queue through Core Reporting.
        </div>
        <div style={{ flex: 1, maxWidth: 520 }}>
          <PaginationControls
            C={C}
            pagination={auditPagination}
            loading={loading}
            error={error}
            emptyLabel="No Crowd audit entries found."
            onPageChange={nextPage => setFilters({ page: nextPage })}
            onPageSizeChange={nextPageSize => setFilters({ page: 1, pageSize: nextPageSize })}
          />
        </div>
      </div>
    </>
  );
}
// ── Screen ────────────────────────────────────────────────────────────────────

export function AdminScreen() {
  const { theme, currentUser, selectedOperation } = useApp();
  const C             = getCrowdC(theme);
  const recordActivity = useCrowdActivityAudit();
  const [tab, setTab] = useState('users');
  const isFullBleedTab = tab === 'venues' || tab === 'catalog';
  const isSystemAdmin = currentUser.role === 'system_admin';

  const usersHook = useUsers();
  const teamsHook = useTeams();
  const operationsHook = useOperations();
  const venuesHook = useVenues();
  const zonesHook = useZones();
  const sim       = useSimulation({ enabled: isSystemAdmin });
  const taxonomyHook = useCrowdTaxonomy();
  const dataFeedsHook = useAdminDataFeeds();
  const auditLogHook = useAdminAuditLog();
  const { toasts, show: showToast, dismiss } = useToast();
  const adminTabs = [
    { id: 'users',   label: 'Users',             count: usersHook.users.length },
    { id: 'roles',   label: 'Roles & Access',    count: crowdRoleDefinitions(C).length },
    { id: 'teams',   label: 'Teams',             count: teamsHook.pagination.total },
    { id: 'venues',  label: 'Venues',            count: venuesHook.venues.length },
    { id: 'catalog', label: 'Zones',             count: zonesHook.zones.length },
    { id: 'presets', label: 'Zone Type Presets',  count: taxonomyHook.taxonomy?.zonePresets.length ?? 0 },
    { id: 'feeds',   label: 'Data Feeds' },
    ...(isSystemAdmin ? [{ id: 'sim', label: 'Simulation Control' }] : []),
    { id: 'audit',   label: 'Audit Log' },
  ];

  useEffect(() => {
    if (tab === 'sim' && !isSystemAdmin) {
      setTab('users');
    }
  }, [isSystemAdmin, tab]);

  const handleTabChange = useCallback((next: string) => {
    setTab(next);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.tabChanged,
      page: '/admin',
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.uiTab,
      resourceId: 'admin',
      metadata: { tab: next, sourcePage: '/admin' },
    });
  }, [recordActivity]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Albert Sans', sans-serif", position: 'relative' }}>
      <div style={{ padding: '0 20px', background: C.surface, borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
        <Tabs C={C} active={tab} onChange={handleTabChange} tabs={adminTabs} />
      </div>
      <div style={{
        flex: 1,
        minHeight: 0,
        padding: isFullBleedTab ? 0 : 20,
        overflow: isFullBleedTab ? 'hidden' : 'auto',
        display: isFullBleedTab ? 'flex' : 'block',
        flexDirection: 'column',
      }}>
        {tab === 'users'   && (
          <UsersTab
            C={C}
            users={usersHook.users}
            loading={usersHook.loading}
            error={usersHook.error}
            createUser={usersHook.createUser}
            updateUser={usersHook.updateUser}
            deactivate={usersHook.deactivate}
            reactivate={usersHook.reactivate}
            onToast={showToast}
          />
        )}
        {tab === 'roles'   && <RolesTab   C={C} users={usersHook.users} />}
        {tab === 'teams'   && (
          <TeamsTab
            C={C}
            teams={teamsHook.teams}
            operations={operationsHook.operations}
            pagination={teamsHook.pagination}
            users={usersHook.users}
            loading={teamsHook.loading || usersHook.loading}
            operationsLoading={operationsHook.loading}
            error={teamsHook.error}
            operationsError={operationsHook.error}
            canArchive={currentUser.role === 'system_admin'}
            setPage={teamsHook.setPage}
            setPageSize={teamsHook.setPageSize}
            createTeam={teamsHook.createTeam}
            updateTeam={teamsHook.updateTeam}
            archiveTeam={teamsHook.archiveTeam}
            onToast={showToast}
          />
        )}
        {tab === 'venues'  && <VenuesScreen />}
        {tab === 'catalog' && <ZonesScreen mode="catalog" />}
        {tab === 'presets' && (
          <ZoneTypePresetsTab
            C={C}
            taxonomy={taxonomyHook.taxonomy}
            loading={taxonomyHook.loading}
            error={taxonomyHook.error}
          />
        )}
        {tab === 'feeds'   && (
          <FeedsTab
            C={C}
            data={dataFeedsHook.data}
            loading={dataFeedsHook.loading}
            error={dataFeedsHook.error}
            refresh={dataFeedsHook.refresh}
          />
        )}
        {tab === 'sim' && isSystemAdmin && <SimulationTab C={C} sim={sim} onToast={showToast} />}
        {tab === 'audit'   && (
          <AuditTab
            C={C}
            data={auditLogHook.data}
            loading={auditLogHook.loading}
            error={auditLogHook.error}
            filters={auditLogHook.filters}
            setFilters={auditLogHook.setFilters}
            refresh={auditLogHook.refresh}
            requestAuditExport={auditLogHook.requestAuditExport}
            operationId={selectedOperation?.id}
            users={usersHook.users}
            onToast={showToast}
          />
        )}
      </div>
      <ToastContainer toasts={toasts} onDismiss={dismiss} C={C} />
    </div>
  );
}
